import { BsEnvelope, BsTrash, BsBoxArrowUpRight, BsPencil } from 'react-icons/bs';

import YesNoFilterControl from '#globalComponents/Table/filters/YesNoFilterControl';
import { CELL_TEXT_MUTED, ROW_ACTION_BUTTON } from '#globalComponents/Table/tableStyles';

function companyInitials(name) {
	if (!name) return '?';
	const parts = name.trim().split(/\s+/);
	const first = parts[0]?.[0] || '';
	const second = parts[1]?.[0] || '';
	return (first + second).toUpperCase() || '?';
}

function formatAddress(co) {
	return [co.addressStreet, [co.addressPostal, co.addressCity].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

/**
 * Column config of the contact-list company-recipients table.
 *
 * @param {Object} params
 * @param {Object} params.filters - Active tab filters (hasEmail, hasAddress)
 * @param {Function} params.onFilterChange - Receives a partial filter object
 * @param {boolean} params.hasCrm - Shows the "Im CRM öffnen" action
 * @param {(company: Object) => void} params.onEdit
 * @param {(companyId: string) => void} params.onOpenCompany
 * @param {(row: Object) => void} params.onRemove
 * @returns {Array<Object>}
 */
export function buildCompanyColumns({ filters, onFilterChange, hasCrm, onEdit, onOpenCompany, onRemove }) {
	return [
		{
			key: 'company',
			label: 'Firma',
			minWidth: 220,
			render: (row) => {
				const co = row.crmCompanies ?? {};
				return (
					<div className="flex items-center gap-3">
						<div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
							{companyInitials(co.companyName)}
						</div>
						<div className="min-w-0">
							<div className="flex items-center gap-1.5">
								<span className="font-semibold text-text truncate" title={co.companyName || undefined}>
									{co.companyName || '—'}
								</span>
								<span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-caution-700 flex-shrink-0">
									Firma
								</span>
							</div>
						</div>
					</div>
				);
			},
		},
		{
			key: 'email',
			label: 'E-Mail',
			minWidth: 200,
			headerAccessory: (
				<YesNoFilterControl
					heading="E-Mail vorhanden"
					paramKey="hasEmail"
					value={filters.hasEmail}
					onChange={onFilterChange}
				/>
			),
			render: (row) => {
				const co = row.crmCompanies ?? {};
				return co.emailAddress ? (
					<span className="text-bgBase-800 flex items-center gap-1 truncate max-w-[200px]">
						<BsEnvelope className="w-2.5 h-2.5 flex-shrink-0 text-primary-400" />
						{co.emailAddress}
					</span>
				) : (
					<span className="text-bgBase-800">—</span>
				);
			},
		},
		{
			key: 'address',
			label: 'Adresse',
			minWidth: 200,
			headerAccessory: (
				<YesNoFilterControl
					heading="Adresse vorhanden"
					paramKey="hasAddress"
					value={filters.hasAddress}
					onChange={onFilterChange}
				/>
			),
			render: (row) => {
				const co = row.crmCompanies ?? {};
				return formatAddress(co) ? (
					<div className="text-bgBase-800 leading-relaxed">
						<div className="truncate max-w-[180px]">{co.addressStreet}</div>
						<div className={CELL_TEXT_MUTED}>
							{co.addressPostal} {co.addressCity}
						</div>
					</div>
				) : (
					<span className="text-bgBase-800">—</span>
				);
			},
		},
		{
			key: 'actions',
			label: '',
			align: 'right',
			sticky: 'right',
			width: 112,
			render: (row) => {
				const co = row.crmCompanies ?? {};
				return (
					<div className="flex items-center justify-end gap-1">
						{co.id && (
							<button onClick={() => onEdit(co)} className={ROW_ACTION_BUTTON} title="Bearbeiten">
								<BsPencil />
							</button>
						)}
						{hasCrm && co.id && (
							<button
								onClick={() => onOpenCompany(co.id)}
								className={ROW_ACTION_BUTTON}
								title="Im CRM öffnen"
							>
								<BsBoxArrowUpRight />
							</button>
						)}
						<button onClick={() => onRemove(row)} className={ROW_ACTION_BUTTON} title="Aus Liste entfernen">
							<BsTrash />
						</button>
					</div>
				);
			},
		},
	];
}
